import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatModal from '../components/ChatModal';
import ClientChatWidget from '../components/ClientChatWidget';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useUnreadChat } from '../hooks/useUnreadChat';
import { BrandSelector, ModelSelector, useBrandsModels } from '../components/BrandModelSelector';
import {
  Cpu,
  LogOut,
  Smartphone,
  ShoppingBag,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Truck,
  User,
  CreditCard,
  ExternalLink,
  MessageCircle,
  Upload,
  Image,
  Download,
  Send,
  Globe,
  ChevronDown,
  StickyNote,
  Eye,
  Paperclip,
  ChevronUp,
  Bell,
  X,
  Plus,
  Minus,
  Trash2,
  Edit3,
  Home,
  Briefcase,
  MapPin,
  FileText,
  Printer,
  Gift,
  Star,
  Lock,
  LifeBuoy
} from 'lucide-react';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ClientPortalHero from '../features/client-portal/ClientPortalHero';
import { BOUTIQUE_TRUST_ITEMS, CLIENT_PORTAL_HEROES } from '../features/client-portal/clientPortalConfig';

// Shared locale-aware date/time formatting options (replace hardcoded 'fr-FR').
const DATETIME_OPTS = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
const DATE_OPTS = { day: '2-digit', month: '2-digit', year: 'numeric' };

const ClientPortalPage = () => {
  const { api, user, logout } = useAuth();
  const { t, lang, setLanguage, languages, formatCurrency, formatDate } = useLanguage();
  const isGuyarepairClient = !!user?.company_id;
  const displayName = isGuyarepairClient ? (user.company_name || t('client.mySpace')) : 'R.E.Mobiles';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reparations');
  const [devices, setDevices] = useState([]);
  const [showDelivered, setShowDelivered] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [onlinePaymentEnabled, setOnlinePaymentEnabled] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const { counts: unreadCounts } = useUnreadChat(api, !!user);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  // Repair request form
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    motif: '', nom_prenom: '', adresse_postale: '', email: '', note: '', tracking_number: '', marque: '', modele: ''
  });
  const { brands: clientBrands, modelsMap: clientModelsMap, seriesMap: clientSeriesMap } = useBrandsModels(api);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [newRequestRef, setNewRequestRef] = useState(null);
  const [trackingInput, setTrackingInput] = useState('');
  const [requestPhotos, setRequestPhotos] = useState([]);
  const [clientDevis, setClientDevis] = useState([]);
  const [clientFactures, setClientFactures] = useState([]);
  const [validationCodes, setValidationCodes] = useState({});
  const [validatingCode, setValidatingCode] = useState(null);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);

  const fetchFidelityData = useCallback(async () => {
    try {
      const [loyaltyRes, referralRes] = await Promise.all([
        api.get('/loyalty/me/status'),
        api.get('/loyalty/me/referral'),
      ]);
      setLoyaltyData(loyaltyRes.data);
      setReferralData(referralRes.data);
    } catch {
      /* garde les données précédentes si rechargement */
    }
  }, [api]);

  // Devis request state
  const [devisRequests, setDevisRequests] = useState([]);
  const [showDevisRequestForm, setShowDevisRequestForm] = useState(false);
  const [devisRequestForm, setDevisRequestForm] = useState({ titre: '', description: '', adresse: '', marque: '', modele: '' });
  const [submittingDevisRequest, setSubmittingDevisRequest] = useState(false);
  const [posInvoices, setPosInvoices] = useState([]);
  const [invoiceSortOrder, setInvoiceSortOrder] = useState('desc');
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [changingPin, setChangingPin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Fetch notification count periodically
  useEffect(() => {
    const fetchNotifCount = async () => {
      try {
        const res = await api.get('/notifications/unread-count');
        setUnreadNotifCount(res.data?.count || 0);
      } catch {}
    };
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 15000);
    return () => clearInterval(interval);
  }, [api]);

  // Fetch payment config to know if online payment is enabled
  useEffect(() => {
    const checkPaymentConfig = async () => {
      try {
        const res = await api.get('/payment-config/status');
        setOnlinePaymentEnabled(res.data?.configured || false);
      } catch {
        setOnlinePaymentEnabled(false);
      }
    };
    checkPaymentConfig();
  }, [api]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
    } catch {}
  };

  const openNotifPanel = async () => {
    setShowNotifPanel(true);
    await fetchNotifications();
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setUnreadNotifCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const markRead = async (notifId) => {
    try {
      await api.post(`/notifications/${notifId}/read`);
      setUnreadNotifCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch {}
  };

  useEffect(() => {
    if (activeTab === 'reparations') {
      fetchDevices();
    } else if (activeTab === 'commandes') {
      fetchOrders();
    } else if (activeTab === 'devis') {
      fetchClientDevis();
      fetchDevisRequests();
      fetchPosInvoices();
      fetchFidelityData();
    }
  }, [activeTab]);

  // Check if must change PIN on first login
  useEffect(() => {
    const checkMustChangePin = async () => {
      try {
        const stored = localStorage.getItem('must_change_pin');
        if (stored === 'true') {
          setShowPinModal(true);
        }
      } catch {}
    };
    checkMustChangePin();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/client/devices');
      setDevices(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/boutique/mes-commandes');
      setOrders(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDevis = async () => {
    setLoading(true);
    try {
      const response = await api.get('/client/devis');
      setClientDevis(response.data?.devis || []);
      setClientFactures(response.data?.factures || []);
    } catch (error) {
      console.error('Error fetching devis:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevisRequests = async () => {
    try {
      const res = await api.get('/client/devis-requests');
      setDevisRequests(res.data || []);
    } catch (err) { console.error('Error fetching devis requests:', err); }
  };

  const fetchPosInvoices = async () => {
    try {
      const res = await api.get('/client/invoices');
      setPosInvoices(res.data || []);
    } catch (err) { console.error('Error fetching invoices:', err); }
  };

  const downloadPosInvoicePdf = async (refVente) => {
    try {
      const res = await api.get(`/client/invoices/${refVente}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Facture_${refVente}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('client.invoiceDownloadError'));
    }
  };

  const viewPosInvoicePdf = async (refVente) => {
    try {
      const res = await api.get(`/client/invoices/${refVente}/pdf?mode=inline`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert(t('client.invoiceViewError'));
    }
  };

  const printPosInvoicePdf = async (refVente) => {
    try {
      const res = await api.get(`/client/invoices/${refVente}/pdf?mode=inline`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'width=900,height=700');
      if (w) {
        const triggerPrint = () => { w.focus(); w.print(); };
        w.addEventListener('load', triggerPrint);
        setTimeout(triggerPrint, 1200);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert(t('client.invoicePrintError'));
    }
  };

  const handleChangePin = async () => {
    if (!newPin || newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      alert(t('client.pinMustBe4'));
      return;
    }
    if (newPin !== confirmPin) {
      alert(t('client.pinMismatch'));
      return;
    }
    setChangingPin(true);
    try {
      await api.post('/client/change-pin', { new_pin: newPin });
      localStorage.removeItem('must_change_pin');
      setShowPinModal(false);
      alert(t('client.pinChanged'));
    } catch (err) {
      alert(err?.response?.data?.detail || t('client.pinChangeError'));
    } finally {
      setChangingPin(false);
    }
  };

  const submitDevisRequest = async () => {
    if (!devisRequestForm.description.trim()) { alert(t('client.describeRequest')); return; }
    setSubmittingDevisRequest(true);
    try {
      await api.post('/client/devis-request', devisRequestForm);
      setDevisRequestForm({ titre: '', description: '', adresse: '', marque: '', modele: '' });
      setShowDevisRequestForm(false);
      fetchDevisRequests();
    } catch (err) {
      alert(err?.response?.data?.detail || t('client.error'));
    } finally {
      setSubmittingDevisRequest(false);
    }
  };

  const getFactureForDevis = (devisId) => {
    return clientFactures.find(f => f.devis_id === devisId);
  };

  const handleValidateCode = async (devisId) => {
    const code = validationCodes[devisId];
    if (!code || code.length < 4) {
      alert(t('client.enterValidationCode'));
      return;
    }
    setValidatingCode(devisId);
    try {
      await api.post(`/devis/validate-code/${devisId}`, { code });
      alert(t('client.codeValidated'));
      fetchClientDevis();
    } catch (err) {
      alert(err?.response?.data?.detail || t('client.codeIncorrect'));
    } finally {
      setValidatingCode(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(isGuyarepairClient ? '/' : '/login');
  };

  const openChat = (relatedType, relatedId, title) => {
    setChatContext({ relatedType, relatedId, title });
  };

  // SumUp Checkout - redirect to SumUp hosted payment page
  const openSumupCheckout = async (orderType, { order_id, device_ref } = {}) => {
    setPaymentLoading(true);
    try {
      const res = await api.post('/payments/sumup/checkout', {
        order_type: orderType,
        order_id: order_id || undefined,
        device_ref: device_ref || undefined,
      });
      const { hosted_url } = res.data;
      if (hosted_url) {
        window.location.href = hosted_url;
      } else {
        alert(t('client.sumupUrlUnavailable'));
        setPaymentLoading(false);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || t('client.sumupCreateError'));
      setPaymentLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Reçu': <Clock className="w-5 h-5 text-cyan-400" />,
      'En Cours': <AlertTriangle className="w-5 h-5 text-yellow-400" />,
      'Attente Pièces': <Package className="w-5 h-5 text-orange-400" />,
      'Terminé': <CheckCircle className="w-5 h-5 text-green-400" />,
      'Livré': <Truck className="w-5 h-5 text-red-400" />,
      'Demande en ligne': <Globe className="w-5 h-5 text-purple-400" />,
      'Non Réparable': <AlertTriangle className="w-5 h-5 text-red-500" />
    };
    return icons[status] || <Clock className="w-5 h-5 text-gray-400" />;
  };

  // Submit repair request
  const submitRepairRequest = async () => {
    if (!requestForm.motif || !requestForm.nom_prenom || !requestForm.adresse_postale || !requestForm.email || !requestForm.marque || !requestForm.modele) {
      alert(t('client.fillRequired'));
      return;
    }
    if (!requestForm.email.includes('@')) {
      alert(t('client.invalidEmail'));
      return;
    }
    setSubmittingRequest(true);
    try {
      const res = await api.post('/client/repair-request', requestForm);
      const createdRef = res.data.ref;
      // Upload photos if any
      if (requestPhotos.length > 0) {
        const fd = new FormData();
        requestPhotos.forEach(f => fd.append('files', f));
        await api.post(`/client/repair-request/${createdRef}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setNewRequestRef(createdRef);
      setShowRequestForm(false);
      setRequestForm({ motif: '', nom_prenom: '', adresse_postale: '', email: '', note: '', tracking_number: '', marque: '', modele: '' });
      setRequestPhotos([]);
      fetchDevices();
    } catch (err) {
      alert(err?.response?.data?.detail || t('client.requestError'));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const submitTrackingNumber = async (ref) => {
    if (!trackingInput.trim()) return;
    try {
      await api.put(`/client/repair-request/${ref}/tracking?tracking_number=${encodeURIComponent(trackingInput.trim())}`);
      setTrackingInput('');
      fetchDevices();
      alert(t('client.trackingSaved'));
    } catch (err) {
      alert(err?.response?.data?.detail || t('client.error'));
    }
  };

  // Status values come from the backend in French — map to i18n keys for DISPLAY only,
  // falling back to the raw value when the status is unknown.
  const getStatusText = (status) => {
    const keys = {
      'Reçu': 'client.statusText.recu',
      'En Cours': 'client.statusText.enCours',
      'Attente Pièces': 'client.statusText.attentePieces',
      'Terminé': 'client.statusText.termine',
      'Livré': 'client.statusText.livre',
      'Demande en ligne': 'client.statusText.demandeEnLigne',
      'Non Réparable': 'client.statusText.nonReparable'
    };
    return keys[status] ? t(keys[status]) : status;
  };

  const getStatusLabel = (status) => {
    const keys = {
      'Reçu': 'client.statusLabel.recu',
      'En Cours': 'client.statusLabel.enCours',
      'Attente Pièces': 'client.statusLabel.attentePieces',
      'Terminé': 'client.statusLabel.termine',
      'Livré': 'client.statusLabel.livre',
      'Récupéré': 'client.statusLabel.recupere',
      'Demande en ligne': 'client.statusLabel.demandeEnLigne',
      'Non Réparable': 'client.statusLabel.nonReparable'
    };
    return keys[status] ? t(keys[status]) : status;
  };

  const getTierLabel = (name) => {
    const keys = { 'Or': 'client.tierOr', 'Argent': 'client.tierArgent', 'Bronze': 'client.tierBronze' };
    return keys[name] ? t(keys[name]) : name;
  };

  const getTimelineEvents = (item) => {
    if (item?.timeline?.length) return item.timeline;
    if (item?.created_at) {
      return [{
        label: t('client.created'),
        status: item.status || item.statut || 'créé',
        timestamp: item.created_at
      }];
    }
    return [];
  };

  const ClientRepairNotes = ({ deviceRef }) => {
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(true);
    const [showNotes, setShowNotes] = useState(false);
    const [expandedAtt, setExpandedAtt] = useState(null);

    useEffect(() => {
      if (showNotes && deviceRef) {
        setLoadingNotes(true);
        api.get(`/repair-notes/${deviceRef}`)
          .then(res => setNotes(res.data || []))
          .catch(() => {})
          .finally(() => setLoadingNotes(false));
      }
    }, [showNotes, deviceRef]);

    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const getUrl = (noteId, fileId) => `${API_BASE}/api/repair-notes/${noteId}/attachments/${fileId}?token=${token}`;
    const fmtDate = (iso) => iso ? formatDate(iso, DATETIME_OPTS) : '';
    const fmtSize = (b) => b > 1024*1024 ? `${(b/(1024*1024)).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;

    return (
      <div className="mt-3 border-t border-white/10 pt-3" data-testid={`client-notes-section-${deviceRef}`}>
        <button
          onClick={() => setShowNotes(!showNotes)}
          className="flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors"
          data-testid={`client-notes-toggle-${deviceRef}`}
        >
          <StickyNote className="w-4 h-4" />
          {t('client.technicianNotes')}
          {showNotes ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        {showNotes && (
          <div className="mt-2 space-y-2" data-testid={`client-notes-list-${deviceRef}`}>
            {loadingNotes ? (
              <div className="flex justify-center py-4"><div className="loading-spinner w-5 h-5"></div></div>
            ) : notes.length === 0 ? (
              <p className="text-xs text-gray-500 py-2">{t('client.noNotes')}</p>
            ) : (
              notes.map(note => (
                <div key={note.id} className="rounded-lg bg-white/5 border border-white/10 p-3" data-testid={`client-note-${note.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-white">{note.author}</span>
                    <span className="text-[10px] text-gray-500">{fmtDate(note.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{note.text}</p>
                  {note.attachments?.length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => setExpandedAtt(expandedAtt === note.id ? null : note.id)}
                        className="flex items-center gap-1 text-[10px] text-cyan-400"
                        data-testid={`client-note-attachments-toggle-${note.id}`}
                      >
                        <Paperclip className="w-3 h-3" />
                        {note.attachments.length} {note.attachments.length > 1 ? t('client.files') : t('client.file')}
                        {expandedAtt === note.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      {expandedAtt === note.id && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {note.attachments.map(att => (
                            <div key={att.id} className="rounded-lg border border-white/10 bg-black/30 overflow-hidden">
                              {att.is_photo ? (
                                <a href={getUrl(note.id, att.id)} target="_blank" rel="noreferrer">
                                  <img src={getUrl(note.id, att.id)} alt={att.filename} className="w-full h-20 object-cover" loading="lazy" />
                                </a>
                              ) : att.is_video ? (
                                <video src={getUrl(note.id, att.id)} className="w-full h-20 object-cover" controls preload="metadata" />
                              ) : (
                                <a href={getUrl(note.id, att.id)} target="_blank" rel="noreferrer" className="h-20 flex items-center justify-center">
                                  <Download className="w-5 h-5 text-gray-500" />
                                </a>
                              )}
                              <div className="p-1">
                                <p className="text-[10px] text-gray-400 truncate">{att.filename}</p>
                                <span className="text-[10px] text-gray-600">{fmtSize(att.size)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen client-portal-v2" data-testid="client-portal-page">
      <div className="grid-pattern"></div>
      
      <div className="client-v2-layout">
        {/* Sidebar V2 (desktop) */}
        <aside className="client-v2-sidebar" data-testid="client-portal-sidebar">
          <div className="client-v2-sidebar-brand">
            <div className="client-v2-brand-icon w-10 h-10 rounded-xl flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-black client-v2-brand-text">{displayName}</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{t('client.space')}</div>
            </div>
          </div>

          <nav className="client-v2-sidenav" data-testid="client-portal-sidenav">
            <div className="client-v2-sidenav-label">{t('client.navigation')}</div>
            <button
              onClick={() => setActiveTab('reparations')}
              className={`client-v2-navitem ${activeTab === 'reparations' ? 'client-v2-navitem-active' : ''}`}
              data-testid="client-nav-reparations"
            >
              <Smartphone className="w-5 h-5" />
              <span>{t('client.repairs')}</span>
              {unreadCounts.device > 0 && <span className="client-v2-nav-badge">{unreadCounts.device}</span>}
            </button>
            <button
              onClick={() => setActiveTab('boutique')}
              className={`client-v2-navitem ${activeTab === 'boutique' ? 'client-v2-navitem-active' : ''}`}
              data-testid="client-nav-boutique"
            >
              <ShoppingBag className="w-5 h-5" />
              <span>{t('client.boutique')}</span>
            </button>
            <button
              onClick={() => setActiveTab('commandes')}
              className={`client-v2-navitem ${activeTab === 'commandes' ? 'client-v2-navitem-active' : ''}`}
              data-testid="client-nav-commandes"
            >
              <Package className="w-5 h-5" />
              <span>{t('client.orders')}</span>
              {unreadCounts.boutique_order > 0 && <span className="client-v2-nav-badge">{unreadCounts.boutique_order}</span>}
            </button>
            <button
              onClick={() => setActiveTab('devis')}
              className={`client-v2-navitem ${activeTab === 'devis' ? 'client-v2-navitem-active' : ''}`}
              data-testid="client-nav-devis"
            >
              <FileText className="w-5 h-5" />
              <span>{t('client.devisFactures')}</span>
            </button>
            <button
              onClick={() => setActiveTab('adresses')}
              className={`client-v2-navitem ${activeTab === 'adresses' ? 'client-v2-navitem-active' : ''}`}
              data-testid="client-nav-adresses"
            >
              <MapPin className="w-5 h-5" />
              <span>{t('client.myAddresses')}</span>
            </button>
            <button
              onClick={() => setShowChangePassword(true)}
              className="client-v2-navitem"
              data-testid="client-nav-account"
            >
              <Lock className="w-5 h-5" />
              <span>{t('client.myAccount')}</span>
            </button>
          </nav>

          <div className="client-v2-sidebar-help" data-testid="client-portal-help-card">
            <p className="text-sm font-bold text-white mb-1">{t('client.needHelp')}</p>
            <p className="text-xs text-gray-400 mb-3">{t('client.helpHours')}</p>
            <button
              onClick={() => openChat('support', null, t('client.contactSupport'))}
              className="w-full py-2 rounded-xl client-v2-btn-primary text-sm"
              data-testid="client-help-contact-btn"
            >
              {t('client.contactUs')}
            </button>
            <button
              onClick={() => navigate('/aide')}
              className="w-full py-2 mt-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border border-white/15 text-white hover:bg-white/5"
              data-testid="client-guide-moi-btn"
              title={t('client.guideMe')}
            >
              <LifeBuoy className="w-4 h-4" />
              {t('client.guideMe')}
            </button>
          </div>

          <button onClick={handleLogout} className="client-v2-sidebar-logout" data-testid="client-logout-sidebar">
            <LogOut className="w-5 h-5" />
            {t('client.logout')}
          </button>
        </aside>

        {/* Content */}
        <div className="client-v2-content">
        {/* Topbar */}
        <header className="client-v2-topbar" data-testid="client-portal-header">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="client-v2-brand-icon w-9 h-9 rounded-xl flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-white" />
            </div>
            <div className="font-black client-v2-brand-text text-sm" data-testid="client-portal-brand">{displayName}</div>
          </div>

        <div className="flex items-center gap-3 ml-auto">
          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-sm"
              data-testid="client-lang-selector-btn"
            >
              <Globe className="w-4 h-4 text-red-400" />
              <span className="text-gray-300">{languages.find(l => l.code === lang)?.flag}</span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </button>
            {langMenuOpen && (
              <>
                <div className="fixed inset-0 z-50" onClick={() => setLangMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-52 max-h-72 overflow-y-auto rounded-xl border border-white/10 bg-gray-900/95 backdrop-blur-xl shadow-2xl z-50" data-testid="client-lang-menu">
                  {languages.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { setLanguage(l.code); setLangMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/10 transition-colors flex items-center gap-3 ${
                        lang === l.code ? 'bg-red-500/10 text-red-400 font-semibold' : 'text-gray-300'
                      }`}
                      data-testid={`client-lang-option-${l.code}`}
                    >
                      <span className="text-base">{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={openNotifPanel}
              className="relative p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              data-testid="client-notif-bell"
            >
              <Bell className="w-5 h-5 text-red-400" />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse" data-testid="notif-unread-badge">
                  {unreadNotifCount}
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm" data-testid="client-user-info">
            <User className="w-4 h-4 text-red-400" />
            <span data-testid="client-username">{user?.username}</span>
          </div>
          <button
            onClick={() => setShowChangePassword(true)}
            className="p-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
            title={t('client.changePassword')}
            data-testid="client-change-password-btn"
          >
            <Lock className="w-5 h-5" />
          </button>
          <button
            onClick={handleLogout}
            className="p-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20"
            data-testid="client-logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="client-portal-main">
        {/* Tabs V2 (mobile only — desktop uses the left sidebar) */}
        <div className="flex gap-1 mb-6 client-v2-tabs overflow-x-auto lg:hidden" data-testid="client-tabs">
          <button
            onClick={() => setActiveTab('reparations')}
            className={`relative client-v2-tab ${activeTab === 'reparations' ? 'client-v2-tab-active' : ''}`}
            data-testid="client-tab-reparations"
          >
            <Smartphone className="w-5 h-5" />
            <span className="hidden sm:inline">{t('client.repairs')}</span>
            {unreadCounts.device > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse" data-testid="unread-badge-reparations">
                {unreadCounts.device}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('boutique')}
            className={`client-v2-tab ${activeTab === 'boutique' ? 'client-v2-tab-active' : ''}`}
            data-testid="client-tab-boutique"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="hidden sm:inline">{t('client.boutique')}</span>
          </button>
          <button
            onClick={() => setActiveTab('commandes')}
            className={`relative client-v2-tab ${activeTab === 'commandes' ? 'client-v2-tab-active' : ''}`}
            data-testid="client-tab-commandes"
          >
            <Package className="w-5 h-5" />
            <span className="hidden sm:inline">{t('client.orders')}</span>
            {unreadCounts.boutique_order > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse" data-testid="unread-badge-commandes">
                {unreadCounts.boutique_order}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('devis')}
            className={`client-v2-tab ${activeTab === 'devis' ? 'client-v2-tab-active' : ''}`}
            data-testid="client-tab-devis"
          >
            <FileText className="w-5 h-5" />
            <span className="hidden sm:inline">{t('client.devisFactures')}</span>
          </button>
          <button
            onClick={() => setActiveTab('adresses')}
            className={`client-v2-tab ${activeTab === 'adresses' ? 'client-v2-tab-active' : ''}`}
            data-testid="client-tab-adresses"
          >
            <MapPin className="w-5 h-5" />
            <span className="hidden sm:inline">{t('client.addresses')}</span>
          </button>
        </div>

        {/* Reparations Tab */}
        {activeTab === 'reparations' && (
          <div className="space-y-4 fade-in">
            <ClientPortalHero
              {...CLIENT_PORTAL_HEROES.reparations}
              testId="client-hero-reparations"
              action={(
                <button
                  type="button"
                  onClick={() => { setShowRequestForm(true); setNewRequestRef(null); }}
                  className="client-v2-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm"
                  data-testid="client-request-repair-btn-hero"
                >
                  <Plus className="w-4 h-4" />
                  {t('client.requestRepair')}
                </button>
              )}
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-bold client-v2-accent sr-only" data-testid="client-repairs-title">{t('client.trackRepairs')}</h2>
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={() => { setShowRequestForm(true); setNewRequestRef(null); }}
                  className="text-xs px-3 py-1.5 rounded-full client-v2-btn-outline sm:hidden"
                  data-testid="client-request-repair-btn"
                >
                  <Plus className="w-3.5 h-3.5 inline mr-1" />
                  {t('client.requestRepair')}
                </button>
                <button
                  onClick={() => setShowDelivered(!showDelivered)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    showDelivered
                      ? 'bg-red-500/15 border-red-500/35 text-red-300'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                  }`}
                  data-testid="client-toggle-delivered"
                >
                  <Truck className="w-3.5 h-3.5 inline mr-1" />
                  {showDelivered ? t('client.hideDelivered') : t('client.showDelivered')}
                </button>
              </div>
            </div>

            {/* Repair Request Form Modal */}
            {showRequestForm && (
              <div className="card-dark p-5 border border-purple-500/30 space-y-4" data-testid="repair-request-form">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-purple-400">{t('client.onlineRepairRequest')}</h3>
                  <button onClick={() => setShowRequestForm(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-sm text-gray-400">{t('client.diagFeeFormPre')} <strong className="text-cyan-400">{formatCurrency(35)}</strong> {t('client.diagFeeFormPost')}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('client.brand')} *</label>
                    <BrandSelector value={requestForm.marque}
                      onChange={v => setRequestForm({...requestForm, marque: v, modele: ''})}
                      brands={clientBrands}
                      testId="request-marque" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('client.model')} *</label>
                    <ModelSelector value={requestForm.modele}
                      onChange={v => setRequestForm({...requestForm, modele: v})}
                      models={clientModelsMap[requestForm.marque] || []}
                      series={clientSeriesMap[requestForm.marque] || {}}
                      brand={requestForm.marque}
                      testId="request-modele" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('client.repairReason')} *</label>
                    <input type="text" value={requestForm.motif} onChange={e => setRequestForm({...requestForm, motif: e.target.value})}
                      placeholder={t('client.repairReasonPlaceholder')} className="input-dark w-full" data-testid="request-motif" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('client.fullName')} *</label>
                    <input type="text" value={requestForm.nom_prenom} onChange={e => setRequestForm({...requestForm, nom_prenom: e.target.value})}
                      placeholder={t('client.fullNamePlaceholder')} className="input-dark w-full" data-testid="request-nom" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('client.postalAddress')} *</label>
                    <input type="text" value={requestForm.adresse_postale} onChange={e => setRequestForm({...requestForm, adresse_postale: e.target.value})}
                      placeholder={t('client.addressPlaceholder')} className="input-dark w-full" data-testid="request-adresse" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('client.emailAddress')} *</label>
                    <input type="email" value={requestForm.email} onChange={e => setRequestForm({...requestForm, email: e.target.value})}
                      placeholder={t('client.emailPlaceholder')} className="input-dark w-full" data-testid="request-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('client.describeIssue')}</label>
                  <textarea value={requestForm.note} onChange={e => setRequestForm({...requestForm, note: e.target.value})}
                    placeholder={t('client.describeIssuePlaceholder')}
                    rows={3} className="input-dark w-full resize-none" data-testid="request-note" />
                </div>
                {/* Photo upload */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('client.devicePhotos')}</label>
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-purple-500/50 transition-colors" data-testid="request-photo-label">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-400">{t('client.addPhotos')}</span>
                    <input type="file" multiple accept="image/*" onChange={e => setRequestPhotos(prev => [...prev, ...Array.from(e.target.files)])} className="hidden" data-testid="request-photo-input" />
                  </label>
                  {requestPhotos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {requestPhotos.map((f, i) => (
                        <div key={i} className="relative group">
                          <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 object-cover rounded-lg border border-white/10" />
                          <button type="button" onClick={() => setRequestPhotos(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowRequestForm(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">{t('client.cancel')}</button>
                  <button onClick={submitRepairRequest} disabled={submittingRequest}
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                    data-testid="request-submit-btn">
                    {submittingRequest ? t('client.sending') : t('client.sendRequest')}
                  </button>
                </div>
              </div>
            )}

            {/* Payment prompt after successful request */}
            {newRequestRef && (
              <div className="card-dark p-5 border border-green-500/30 space-y-4" data-testid="request-payment-prompt">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <h3 className="text-lg font-bold text-green-400">{t('client.requestSaved', { ref: newRequestRef })}</h3>
                </div>
                <p className="text-sm text-gray-300">{t('client.diagFeePending')} <strong className="text-cyan-400">{formatCurrency(35)}</strong>.</p>

                {onlinePaymentEnabled && (
                  <button
                    onClick={() => openSumupCheckout('repair', { device_ref: newRequestRef })}
                    disabled={paymentLoading}
                    className="w-full py-3 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                    style={{ background: '#1A4CFF' }}
                    data-testid="request-pay-btn"
                  >
                    <CreditCard className="w-5 h-5 inline mr-2" />
                    {paymentLoading ? t('client.redirecting') : t('client.payDiagnosticSumup', { amount: formatCurrency(35) })}
                  </button>
                )}

                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm text-gray-400 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1 text-cyan-400" />
                    {t('client.depositOrSend')}
                  </p>
                  {!isGuyarepairClient && <p className="text-sm font-semibold text-white ml-5">R.E.Mobiles - 102 Avenue General de Gaulle, Porte 2, 97300 Cayenne</p>}
                  <div className="flex gap-2 mt-3">
                    <input type="text" value={trackingInput} onChange={e => setTrackingInput(e.target.value)}
                      placeholder={t('client.trackingPlaceholder')}
                      className="input-dark flex-1 text-sm" data-testid="request-tracking-input" />
                    <button onClick={() => submitTrackingNumber(newRequestRef)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors"
                      data-testid="request-tracking-btn">
                      {t('client.save')}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('client.postalDelayNotice')}</p>
                </div>
                <button onClick={() => setNewRequestRef(null)} className="text-xs text-gray-500 hover:text-gray-300">{t('client.close')}</button>
              </div>
            )}
            
            {loading ? (
              <div className="flex justify-center py-12" data-testid="client-repairs-loading">
                <div className="loading-spinner"></div>
              </div>
            ) : devices.filter(d => showDelivered || (d.status !== 'Livré' && d.status !== 'Récupéré')).length === 0 ? (
              <div className="text-center py-12 text-gray-400" data-testid="client-repairs-empty">
                <Smartphone className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p data-testid="client-repairs-empty-text">{showDelivered ? t('client.noRepairs') : t('client.noRepairsInProgress')}</p>
              </div>
            ) : (
              devices.filter(d => showDelivered || (d.status !== 'Livré' && d.status !== 'Récupéré')).map((device) => (
                <div key={device.id} className="client-device-card" data-testid={`client-repair-card-${device.ref}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="text-cyan-400 font-bold client-v2-accent" data-testid={`client-repair-ref-${device.ref}`}>{device.ref}</span>
                      <h3 className="text-lg font-bold" data-testid={`client-repair-model-${device.ref}`}>{device.model}</h3>
                      {device.problem && (
                        <p className="text-sm text-gray-400" data-testid={`client-repair-problem-${device.ref}`}>{device.problem}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10" data-testid={`client-repair-status-${device.ref}`}>
                      {getStatusIcon(device.status)}
                      <span className="text-sm font-semibold" data-testid={`client-repair-status-text-${device.ref}`}>{getStatusLabel(device.status)}</span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4" data-testid={`client-repair-progress-${device.ref}`}>
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      <span>{t('client.progress')}</span>
                      <span>{device.progress}%</span>
                    </div>
                    <div className="progress-bar-custom">
                      <div className="progress-fill" style={{ width: `${device.progress}%` }}></div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-300 mb-4" data-testid={`client-repair-status-description-${device.ref}`}>
                    {getStatusText(device.status)}
                  </p>

                  {/* Payment Info */}
                  <div className="flex justify-between items-center p-3 rounded-xl bg-black/30 border border-white/5 mb-3" data-testid={`client-repair-payment-info-${device.ref}`}>
                    <div>
                      <div className="text-sm text-gray-400">{t('client.total')}</div>
                      <div className="font-bold">{formatCurrency(device.price_repair + device.price_diagnostic)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">{t('client.deposits')}</div>
                      <div className="font-bold text-green-400">{formatCurrency(device.total_acomptes ?? 0)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">{t('client.rest')}</div>
                      <div className="font-bold text-red-400">{formatCurrency(device.reste_a_payer ?? 0)}</div>
                    </div>
                  </div>

                  {/* Pay button */}
                  {device.reste_a_payer > 0 && onlinePaymentEnabled && (
                    <button
                      onClick={() => openSumupCheckout('repair', { device_ref: device.ref })}
                      className="w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2"
                      style={{ background: '#1A4CFF' }}
                      data-testid={`sumup-pay-repair-${device.ref}`}
                      disabled={paymentLoading}
                    >
                      {paymentLoading ? (
                        <div className="loading-spinner w-5 h-5"></div>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5" />
                          {t('client.payWithSumup', { amount: formatCurrency(device.reste_a_payer ?? 0) })}
                        </>
                      )}
                    </button>
                  )}

                  {/* Timeline + Chat */}
                  <div className="mt-4 border-t border-white/10 pt-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-cyan-200">{t('client.detailedTracking')}</h4>
                      <button
                        onClick={() => openChat('device', device.ref, t('client.chatRepair', { ref: device.ref }))}
                        className="text-xs px-3 py-1.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 flex items-center gap-1.5 transition-colors"
                        data-testid={`client-repair-chat-btn-${device.ref}`}
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {t('client.contact')}
                      </button>
                    </div>
                    <div className="mt-3 relative pl-4">
                      <div className="absolute left-1.5 top-1 bottom-1 w-px bg-gradient-to-b from-cyan-500/50 to-transparent"></div>
                      {getTimelineEvents(device).map((event, idx) => (
                        <div key={`${device.ref}-tl-${idx}`} className="relative pb-3 last:pb-0" data-testid={`repair-timeline-event-${device.ref}-${idx}`}>
                          <div className="absolute -left-[10.5px] mt-1.5 w-2 h-2 rounded-full bg-cyan-400 ring-2 ring-cyan-400/20"></div>
                          <div className="ml-2">
                            <p className="text-xs font-semibold text-white">{event.label}</p>
                            <p className="text-[10px] text-gray-500">{event.timestamp ? formatDate(event.timestamp, DATETIME_OPTS) : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Public Notes */}
                  <ClientRepairNotes deviceRef={device.ref} />

                  {/* Photos */}
                  {device.photos && device.photos.length > 0 && (
                    <div className="mt-3" data-testid={`client-repair-photos-${device.ref}`}>
                      <p className="text-xs text-gray-400 mb-2">{t('client.photosCount', { count: device.photos.length })}</p>
                      <div className="flex flex-wrap gap-2">
                        {device.photos.map((url, i) => (
                          <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noreferrer">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-cyan-500/50 transition-colors" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Boutique Tab */}
        {activeTab === 'boutique' && (
          <>
            <ClientPortalHero {...CLIENT_PORTAL_HEROES.boutique} testId="client-hero-boutique" />
            <ClientBoutique
              api={api}
              openSumupCheckout={openSumupCheckout}
              paymentLoading={paymentLoading}
              onlinePaymentEnabled={onlinePaymentEnabled}
              onManageAddresses={() => setActiveTab('adresses')}
            />
          </>
        )}

        {activeTab === 'adresses' && (
          <ClientAddresses api={api} />
        )}

        {activeTab === 'commandes' && (
          <div className="space-y-4 fade-in">
            <ClientPortalHero {...CLIENT_PORTAL_HEROES.commandes} testId="client-hero-commandes" />
            <h2 className="text-lg font-bold client-v2-accent sr-only" data-testid="client-orders-title">{t('client.trackOrders')}</h2>
            
            {loading ? (
              <div className="flex justify-center py-12" data-testid="client-orders-loading">
                <div className="loading-spinner"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 text-gray-400" data-testid="client-orders-empty">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p data-testid="client-orders-empty-text">{t('client.noOrders')}</p>
              </div>
            ) : (
              orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  api={api}
                  openSumupCheckout={openSumupCheckout}
                  paymentLoading={paymentLoading}
                  openChat={openChat}
                  getTimelineEvents={getTimelineEvents}
                  onRefresh={fetchOrders}
                  onlinePaymentEnabled={onlinePaymentEnabled}
                />
              ))
            )}
          </div>
        )}

      {/* Devis Tab */}
      {activeTab === 'devis' && (
        <div className="space-y-4 fade-in" data-testid="client-devis-section">
          <ClientPortalHero {...CLIENT_PORTAL_HEROES.devis} testId="client-hero-devis" />

          {/* Fidélité + Parrainage — toujours visibles sous le hero Devis/Factures */}
          <div className="grid md:grid-cols-2 gap-4 items-start" data-testid="client-fidelity-section">
          {/* Loyalty Widget */}
          {loyaltyData && (
            <div className="card-dark p-5 border border-amber-500/20" data-testid="client-loyalty-widget">
              {/* Level Badge + Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: loyaltyData.level?.name === 'Or' ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                        : loyaltyData.level?.name === 'Argent' ? 'linear-gradient(135deg, #9ca3af, #6b7280)'
                        : 'linear-gradient(135deg, #cd7f32, #a0522d)',
                    }}>
                    <span className="text-xl font-black text-white">{loyaltyData.level?.name === 'Or' ? 'OR' : loyaltyData.level?.name === 'Argent' ? 'AG' : 'BR'}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white text-base">{t('client.level', { name: getTierLabel(loyaltyData.level?.name || 'Bronze') })}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{
                          background: loyaltyData.level?.name === 'Or' ? 'rgba(251,191,36,0.15)' : loyaltyData.level?.name === 'Argent' ? 'rgba(156,163,175,0.15)' : 'rgba(205,127,50,0.15)',
                          color: loyaltyData.level?.name === 'Or' ? '#fbbf24' : loyaltyData.level?.name === 'Argent' ? '#9ca3af' : '#cd7f32',
                        }}>
                        -{loyaltyData.level?.discount || 5}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">{t('client.totalPurchases', { count: loyaltyData.total_purchases })}</p>
                  </div>
                </div>
              </div>

              {/* Level perks */}
              {loyaltyData.level?.perks && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {loyaltyData.level.perks.map((perk, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-lg text-[11px] font-medium"
                      style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }}>
                      {perk}
                    </span>
                  ))}
                </div>
              )}

              {/* Progress to next level */}
              {loyaltyData.level?.next_level && (
                <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400">{t('client.nextLevel')} <span className="font-bold text-white">{getTierLabel(loyaltyData.level.next_level)}</span></span>
                    <span className="text-xs font-bold" style={{ color: loyaltyData.level.next_level === 'Or' ? '#fbbf24' : '#9ca3af' }}>
                      {t('client.purchasesToNext', { count: loyaltyData.level.purchases_to_next })}
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${loyaltyData.level.progress}%`,
                        background: loyaltyData.level.next_level === 'Or' ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #6b7280, #9ca3af)',
                      }} />
                  </div>
                </div>
              )}

              {loyaltyData.has_pending_reward ? (
                <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/15 to-yellow-500/15 border border-amber-500/30" data-testid="loyalty-reward-banner">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-3xl">&#127873;</div>
                    <div>
                      <div className="font-bold text-amber-300 text-lg">{t('client.rewardAvailable')}</div>
                      <div className="text-sm text-amber-400/80">{t('client.reward10Reached', { count: 10 })}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                      <div className="text-2xl font-black text-amber-400">-15%</div>
                      <div className="text-xs text-amber-300/70 mt-1">{t('client.rewardPosDesc')}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-center">
                      <div className="text-2xl font-black text-cyan-400">-10%</div>
                      <div className="text-xs text-cyan-300/70 mt-1">{t('client.rewardRepairDesc')}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300 flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-amber-400" />
                      {t('client.loyaltyPoints')}
                    </span>
                    <span className="text-sm font-bold text-amber-400" data-testid="loyalty-points-display">{loyaltyData.current_points} / 10</span>
                  </div>
                  <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${(loyaltyData.current_points / 10) * 100}%`,
                        background: 'linear-gradient(90deg, #f59e0b, #eab308, #10b981)'
                      }}
                      data-testid="loyalty-progress-bar"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      {loyaltyData.purchases_until_reward > 0
                        ? t('client.purchasesUntilReward', { count: loyaltyData.purchases_until_reward })
                        : t('client.rewardReached')}
                    </span>
                  </div>
                </div>
              )}

              {loyaltyData.rewards_history?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="text-xs text-gray-400 mb-2">{t('client.rewardsHistory')}</div>
                  {loyaltyData.rewards_history.slice(-3).reverse().map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5">
                      <span className="text-emerald-400">{r.type === 'pos_15' ? t('client.reward15Pos') : t('client.reward10Repair')}</span>
                      <span className="text-gray-500">{r.used_at ? formatDate(r.used_at) : '-'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Referral / Parrainage Section */}
          {referralData && (
            <div className="card-dark p-5 space-y-4" data-testid="referral-section">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-white text-base">{t('client.referralProgram')}</h3>
                  <p className="text-xs text-gray-400">{t('client.referralSubtitle')}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-cyan-500/15 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-cyan-400" />
                </div>
              </div>

              {/* Referral Code */}
              <div className="rounded-xl p-4 border border-cyan-500/20" style={{ background: 'rgba(6,182,212,0.06)' }}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{t('client.yourReferralCode')}</div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-black tracking-[0.2em] text-cyan-400 font-mono" data-testid="referral-code">
                    {referralData.code}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(referralData.code);
                      setReferralCopied(true);
                      setTimeout(() => setReferralCopied(false), 2000);
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                    style={{
                      background: referralCopied ? 'rgba(34,197,94,0.15)' : 'rgba(6,182,212,0.15)',
                      color: referralCopied ? '#22c55e' : '#06b6d4',
                      border: referralCopied ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(6,182,212,0.3)',
                    }}
                    data-testid="copy-referral-code-btn"
                  >
                    {referralCopied ? t('client.copied') : t('client.copy')}
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  {t('client.referralShare1')} <span className="text-cyan-400 font-bold">{t('client.referralBonusPoints')}</span> {t('client.referralShare2')}
                </p>
              </div>

              {/* Share Link */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const link = `${window.location.origin}/inscription-client?ref=${referralData.code}`;
                    navigator.clipboard.writeText(link);
                    setReferralCopied(true);
                    setTimeout(() => setReferralCopied(false), 2000);
                  }}
                  className="flex-1 py-2.5 rounded-xl text-xs font-bold text-center transition-all"
                  style={{ background: 'rgba(6,182,212,0.12)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.2)' }}
                  data-testid="copy-referral-link-btn"
                >
                  {t('client.copyReferralLink')}
                </button>
              </div>

              {/* Referral Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="text-xl font-black text-cyan-400">{referralData.total_referrals || 0}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{t('client.referrals')}</div>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="text-xl font-black text-amber-400">{referralData.bonus_points_earned || 0}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{t('client.bonusPoints')}</div>
                </div>
              </div>

              {/* Referral List */}
              {referralData.referrals?.length > 0 && (
                <div className="pt-3 border-t border-white/10">
                  <div className="text-xs text-gray-400 mb-2">{t('client.yourReferrals')}</div>
                  {referralData.referrals.slice(-5).reverse().map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5">
                      <span className="text-white">{r.nom || r.email || r.telephone || t('client.referralFallback')}</span>
                      <span className="text-gray-500">{r.referred_at ? formatDate(r.referred_at) : '-'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold client-v2-accent" data-testid="client-devis-title">{t('client.myDevis')}</h2>
            {!showDevisRequestForm && (
              <button
                onClick={() => setShowDevisRequestForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl client-v2-btn-outline text-sm transition-all"
                data-testid="client-new-devis-request-btn"
              >
                <Plus className="w-4 h-4" /> {t('client.newRequest')}
              </button>
            )}
          </div>

          {/* Devis Request Form */}
          {showDevisRequestForm && (
            <div className="card-dark p-5 space-y-4" data-testid="client-devis-request-form">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-white">{t('client.newDevisRequest')}</h3>
                <button onClick={() => setShowDevisRequestForm(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('client.titleOptional')}</label>
                <input
                  value={devisRequestForm.titre}
                  onChange={e => setDevisRequestForm(f => ({ ...f, titre: e.target.value }))}
                  className="input-dark w-full py-2"
                  placeholder={t('client.devisTitlePlaceholder')}
                  data-testid="devis-request-titre"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('client.requestDescription')} *</label>
                <textarea
                  value={devisRequestForm.description}
                  onChange={e => setDevisRequestForm(f => ({ ...f, description: e.target.value }))}
                  className="input-dark w-full py-2 min-h-[100px]"
                  placeholder={t('client.devisDescPlaceholder')}
                  data-testid="devis-request-description"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('client.addressOptional')}</label>
                <input
                  value={devisRequestForm.adresse}
                  onChange={e => setDevisRequestForm(f => ({ ...f, adresse: e.target.value }))}
                  className="input-dark w-full py-2"
                  placeholder={t('client.devisAddressPlaceholder')}
                  data-testid="devis-request-adresse"
                />
              </div>
              <button
                onClick={submitDevisRequest}
                disabled={submittingDevisRequest}
                className="w-full py-3 rounded-xl bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/30 font-bold transition-all"
                data-testid="devis-request-submit"
              >
                <Send className="w-4 h-4 inline mr-2" />
                {submittingDevisRequest ? t('client.sending') : t('client.sendMyRequest')}
              </button>
            </div>
          )}

          {/* Existing Devis Requests */}
          {devisRequests.length > 0 && (
            <div className="space-y-3" data-testid="client-devis-requests-list">
              <h3 className="text-sm font-semibold text-cyan-400">{t('client.myDevisRequests')}</h3>
              {devisRequests.map(req => (
                <div key={req.id} className="card-dark p-4 space-y-2" data-testid={`devis-request-${req.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-white">{req.titre || t('client.devisRequestTitle')}</div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold border ${
                      req.status === 'nouveau' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                      req.status === 'en_cours' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      req.status === 'refuse' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      'bg-green-500/20 text-green-400 border-green-500/30'
                    }`}>
                      {req.status === 'nouveau' ? t('client.devisReqNouveau') : req.status === 'en_cours' ? t('client.devisReqEnCours') : req.status === 'refuse' ? t('client.devisReqRefuse') : t('client.devisReqTraite')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300">{req.description}</p>
                  {req.adresse && <p className="text-xs text-gray-500"><MapPin className="w-3 h-3 inline mr-1" />{req.adresse}</p>}
                  {req.staff_note && (
                    <div className="mt-2 p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-300">
                      <strong>{t('client.teamResponse')}</strong> {req.staff_note}
                    </div>
                  )}
                  {req.motif_refus && (
                    <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">
                      <strong>{t('client.refusalReason')}</strong> {req.motif_refus}
                    </div>
                  )}
                  <div className="text-xs text-gray-600">{req.created_at ? formatDate(req.created_at, DATETIME_OPTS) : ''}</div>
                </div>
              ))}
            </div>
          )}

          {/* Existing Devis List */}
          {loading ? (
            <div className="flex justify-center py-12"><div className="loading-spinner"></div></div>
          ) : clientDevis.length === 0 && devisRequests.length === 0 && !showDevisRequestForm ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>{t('client.noDevis')}</p>
              <p className="text-sm mt-2">{t('client.clickNewRequest')}</p>
            </div>
          ) : clientDevis.length > 0 && (
            clientDevis.map(devis => {
              const facture = getFactureForDevis(devis.id);
              const amountPaid = facture ? parseFloat(facture.amount_paid || 0) : 0;
              const totalAmount = parseFloat(devis.total || 0);
              const remaining = Math.max(0, totalAmount - amountPaid);
              const stColors = {
                en_attente: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                accepte: 'bg-green-500/20 text-green-400 border-green-500/30',
                refuse: 'bg-red-500/20 text-red-400 border-red-500/30',
              };
              const paymentColors = {
                acompte_verse: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                paye: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
              };
              const stLabels = {
                en_attente: 'En attente',
                accepte: 'Accepte',
                refuse: 'Refuse',
              };
              const paymentLabels = {
                acompte_verse: 'Acompte verse',
                paye: 'Paye',
                partial: 'Acompte verse',
                paid: 'Paye',
              };
              const effectivePaymentStatus = devis.payment_status || (facture?.payment_status) || '';
              const displayStatus = effectivePaymentStatus && paymentLabels[effectivePaymentStatus]
                ? paymentLabels[effectivePaymentStatus]
                : stLabels[devis.status] || devis.status;
              const displayColor = effectivePaymentStatus && paymentColors[effectivePaymentStatus]
                ? paymentColors[effectivePaymentStatus]
                : (effectivePaymentStatus === 'partial' ? paymentColors.acompte_verse
                : effectivePaymentStatus === 'paid' ? paymentColors.paye
                : stColors[devis.status] || stColors.en_attente);

              return (
                <div key={devis.id} className="card-dark p-4 space-y-3" data-testid={`client-devis-${devis.ref}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-violet-400">{devis.ref}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {devis.created_at ? formatDate(devis.created_at) : ''}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold border ${displayColor}`}>
                      {displayStatus}
                    </span>
                  </div>

                  {devis.description && (
                    <p className="text-sm text-gray-400">{devis.description}</p>
                  )}

                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-white/5">
                          <th className="text-left p-2 text-gray-400 text-xs">{t('client.designation')}</th>
                          <th className="text-right p-2 text-gray-400 text-xs w-20">{t('client.price')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devis.lignes?.map((l, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="p-2 text-gray-300 text-xs">{l.designation} x{l.quantite}</td>
                            <td className="p-2 text-right text-white text-xs">{formatCurrency(parseFloat(l.prix) * parseInt(l.quantite))}</td>
                          </tr>
                        ))}
                        {devis.inclure_deplacement && (
                          <tr className="border-t border-white/5">
                            <td className="p-2 text-gray-300 text-xs">{t('client.travelFees')}</td>
                            <td className="p-2 text-right text-white text-xs">{formatCurrency(parseFloat(devis.frais_deplacement || 45))}</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-white/10 bg-white/5">
                          <td className="p-2 text-right font-bold text-white text-xs">{t('client.totalUpper')}</td>
                          <td className="p-2 text-right font-bold text-violet-400">{formatCurrency(totalAmount)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Payment summary - only show after code validation */}
                  {facture && devis.code_validated && (devis.status === 'accepte' || effectivePaymentStatus) && (
                    <div className="flex justify-between items-center p-3 rounded-xl bg-black/30 border border-white/5" data-testid={`devis-payment-info-${devis.ref}`}>
                      <div>
                        <div className="text-xs text-gray-400">{t('client.total')}</div>
                        <div className="font-bold text-white">{formatCurrency(totalAmount)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">{t('client.paid')}</div>
                        <div className="font-bold text-green-400">{formatCurrency(amountPaid)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">{t('client.rest')}</div>
                        <div className={`font-bold ${remaining > 0 ? 'text-amber-400' : 'text-green-400'}`}>{formatCurrency(remaining)}</div>
                      </div>
                    </div>
                  )}

                  {devis.bon_commande_ref && (
                    <div className="text-xs text-gray-400">
                      {t('client.purchaseOrder')}: <span className="text-green-400 font-semibold">{devis.bon_commande_ref}</span>
                    </div>
                  )}
                  {devis.facture_ref && (
                    <div className="text-xs text-gray-400">
                      {t('client.invoice')}: <span className="text-red-400 font-semibold">{devis.facture_ref}</span>
                    </div>
                  )}

                  {/* Late payment penalties warning */}
                  {facture?.late_penalties && remaining > 0 && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 space-y-1" data-testid={`devis-penalties-${devis.ref}`}>
                      <p className="text-xs text-red-400 font-bold">{t('client.latePayment', { days: facture.late_penalties.days_late })}</p>
                      <p className="text-xs text-red-300">{t('client.latePenaltyLabel', { rate: facture.late_penalties.taux_penalite })} : {formatCurrency(facture.late_penalties.penalite_retard)}</p>
                      <p className="text-xs text-red-300">{t('client.recoveryIndemnity')} : {formatCurrency(facture.late_penalties.indemnite_forfaitaire)}</p>
                      <p className="text-xs text-red-400 font-bold mt-1">{t('client.totalWithPenalties')} : {formatCurrency(facture.late_penalties.total_with_penalties)}</p>
                    </div>
                  )}

                  {/* Validation code input */}
                  {devis.status === 'accepte' && devis.validation_code && !devis.code_validated && (
                    <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 space-y-2" data-testid={`devis-validation-section-${devis.ref}`}>
                      <p className="text-xs text-amber-400 font-semibold">{t('client.validationCodeTitle')}</p>
                      <p className="text-xs text-gray-400">{t('client.validationCodeDesc')}</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={validationCodes[devis.id] || ''}
                          onChange={e => setValidationCodes(prev => ({ ...prev, [devis.id]: e.target.value }))}
                          placeholder={t('client.code6digits')}
                          maxLength={6}
                          className="input-dark flex-1 text-sm text-center tracking-widest font-mono"
                          data-testid={`devis-validation-input-${devis.ref}`}
                        />
                        <button
                          onClick={() => handleValidateCode(devis.id)}
                          disabled={validatingCode === devis.id}
                          className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                          data-testid={`devis-validation-btn-${devis.ref}`}
                        >
                          {validatingCode === devis.id ? '...' : t('client.validate')}
                        </button>
                      </div>
                    </div>
                  )}

                  {devis.code_validated && (
                    <div className="flex items-center gap-2 text-xs text-green-400" data-testid={`devis-code-validated-${devis.ref}`}>
                      <CheckCircle className="w-4 h-4" /> {t('client.codeConfirmed')}
                    </div>
                  )}

                  {/* PDF downloads */}
                  <div className="flex gap-2 flex-wrap">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/${devis.id}/pdf`} target="_blank" rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30 text-xs font-semibold flex items-center gap-1"
                      data-testid={`devis-pdf-btn-${devis.ref}`}>
                      <Download className="w-3 h-3" /> {t('client.pdfDevis')}
                    </a>
                    {devis.bon_commande_ref && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/devis/${devis.id}/bon-pdf`} target="_blank" rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 text-xs font-semibold flex items-center gap-1"
                        data-testid={`devis-bon-pdf-btn-${devis.ref}`}>
                        <Download className="w-3 h-3" /> {t('client.pdfPurchaseOrder')}
                      </a>
                    )}
                    {facture && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/facture-dep/${facture.id}/pdf`} target="_blank" rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 text-xs font-semibold flex items-center gap-1"
                        data-testid={`devis-facture-pdf-btn-${devis.ref}`}>
                        <Download className="w-3 h-3" /> {t('client.pdfInvoice')}
                      </a>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {devis.status === 'en_attente' && (
                      <a href={`/devis/accepter/${devis.id}`}
                        className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold flex items-center gap-1"
                        data-testid={`devis-accept-btn-${devis.ref}`}>
                        <CheckCircle className="w-3 h-3" /> {t('client.acceptRefuse')}
                      </a>
                    )}
                    {devis.status === 'accepte' && devis.code_validated && remaining > 0 && (
                      <>
                        <a href={`/devis/paiement/${devis.id}?mode=acompte`}
                          className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-semibold flex items-center gap-1"
                          data-testid={`devis-pay-acompte-btn-${devis.ref}`}>
                          <CreditCard className="w-3 h-3" /> {amountPaid > 0 ? t('client.payRest', { amount: formatCurrency(remaining) }) : t('client.deposit50')}
                        </a>
                        {amountPaid === 0 && (
                          <a href={`/devis/paiement/${devis.id}?mode=complet`}
                            className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 text-xs font-semibold flex items-center gap-1"
                            data-testid={`devis-pay-complet-btn-${devis.ref}`}>
                            <CreditCard className="w-3 h-3" /> {t('client.fullPayment')}
                          </a>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {/* FACTURES POS */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-red-400" data-testid="client-invoices-title">{t('client.myInvoices')}</h2>
              <button
                onClick={() => setInvoiceSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                className="px-3 py-1.5 rounded-lg bg-gray-700/50 text-gray-300 border border-gray-600/30 text-xs font-semibold flex items-center gap-1 hover:bg-gray-700"
                data-testid="invoice-sort-btn"
              >
                <Clock className="w-3 h-3" />
                {invoiceSortOrder === 'desc' ? t('client.mostRecent') : t('client.oldest')}
              </button>
            </div>

            {posInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t('client.noInvoices')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const sorted = [...posInvoices].sort((a, b) => invoiceSortOrder === 'desc'
                    ? new Date(b.created_at) - new Date(a.created_at)
                    : new Date(a.created_at) - new Date(b.created_at)
                  );
                  const visible = showAllInvoices ? sorted : sorted.slice(0, 3);
                  return (
                    <>
                      {visible.map(inv => (
                    <div key={inv.id} className="card-dark p-4 rounded-xl border border-gray-700/50" data-testid={`pos-invoice-${inv.ref_vente}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-white">{inv.ref_vente}</span>
                        <span className="text-xs text-gray-400">
                          {formatDate(inv.created_at, DATE_OPTS)}
                        </span>
                      </div>
                      <div className="space-y-1 mb-3">
                        {(inv.items || []).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-300">{item.designation} x{item.quantite}</span>
                            <span className="text-white font-medium">{formatCurrency(item.prix_unitaire || item.prix || item.prix_total || 0)}</span>
                          </div>
                        ))}
                      </div>
                      {inv.mode_paiement && (
                        <div className="mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{inv.mode_paiement}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-2">
                        <span>{t('client.paid')}: <span className="text-green-400 font-semibold">{formatCurrency(inv.montant_paye ?? inv.total ?? 0)}</span></span>
                        {(inv.reste_a_payer || 0) > 0.01 && (
                          <span>{t('client.rest')}: <span className="text-amber-400 font-semibold">{formatCurrency(inv.reste_a_payer)}</span></span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-green-400" data-testid={`invoice-total-${inv.ref_vente}`}>{formatCurrency(inv.total || 0)}</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewPosInvoicePdf(inv.ref_vente)}
                            className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 text-xs font-semibold flex items-center gap-1"
                            data-testid={`pos-invoice-view-${inv.ref_vente}`}
                          >
                            <Eye className="w-3 h-3" /> {t('client.view')}
                          </button>
                          <button
                            onClick={() => printPosInvoicePdf(inv.ref_vente)}
                            className="px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 text-xs font-semibold flex items-center gap-1"
                            data-testid={`pos-invoice-print-${inv.ref_vente}`}
                          >
                            <Printer className="w-3 h-3" /> {t('client.print')}
                          </button>
                          <button
                            onClick={() => downloadPosInvoicePdf(inv.ref_vente)}
                            className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 text-xs font-semibold flex items-center gap-1"
                            data-testid={`pos-invoice-download-${inv.ref_vente}`}
                          >
                            <Download className="w-3 h-3" /> PDF
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                      {sorted.length > 3 && (
                        <button
                          onClick={() => setShowAllInvoices(prev => !prev)}
                          className="w-full py-3 rounded-xl bg-gray-700/50 border border-gray-600/30 text-gray-300 text-sm font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                          data-testid="show-more-invoices-btn"
                        >
                          {showAllInvoices ? (
                            <><ChevronUp className="w-4 h-4" /> {t('client.seeLess')}</>
                          ) : (
                            <><ChevronDown className="w-4 h-4" /> {t('client.seeMoreInvoices', { count: sorted.length - 3 })}</>
                          )}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* ═══ MENTIONS DE CONSERVATION ═══ */}
          <div className="mt-6 p-4 rounded-xl bg-[#151820] border border-white/10 space-y-1" data-testid="retention-notice">
            <p className="text-xs text-gray-500">{t('client.invoiceRetention')}</p>
            <p className="text-xs text-gray-500">{t('client.devisRetention')}</p>
          </div>
        </div>
      )}


        {/* Caricature Button for Clients - only for R.E.Mobiles */}
        {!isGuyarepairClient && (
        <div className="mt-8 text-center">
          <a
            href="https://caricaturerem.com"
            target="_blank"
            rel="noopener noreferrer"
            className="client-caricature-btn inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold text-lg text-pink-200"
            data-testid="client-caricature-link"
          >
            <span className="client-caricature-sparkle" aria-hidden="true">✨</span>
            {t('client.createCaricature')}
          </a>
        </div>
        )}

        <p className="text-center mt-4 text-xs text-gray-600">
          {t('client.copyrightBy')} {isGuyarepairClient ? displayName : 'REMOBILES'}
        </p>
      </main>
        </div>
      </div>

      {/* Notification Panel */}
      {showNotifPanel && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-16" data-testid="notif-panel-overlay">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowNotifPanel(false)} />
          <div className="relative w-full max-w-md mx-4 rounded-2xl border border-white/10 bg-gray-900/95 backdrop-blur-xl shadow-2xl overflow-hidden" data-testid="notif-panel">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-bold text-cyan-400 flex items-center gap-2">
                <Bell className="w-5 h-5" /> {t('client.notifications')}
              </h3>
              <div className="flex items-center gap-2">
                {unreadNotifCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors"
                    data-testid="notif-mark-all-read"
                  >
                    {t('client.markAllRead')}
                  </button>
                )}
                <button onClick={() => setShowNotifPanel(false)} className="text-gray-400 hover:text-white" data-testid="notif-panel-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3 space-y-2">
              {notifications.length === 0 ? (
                <div className="text-center py-8 text-gray-500" data-testid="notif-empty">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">{t('client.noNotifications')}</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`p-3 rounded-xl border transition-all cursor-pointer ${
                      notif.read
                        ? 'bg-white/5 border-white/5'
                        : 'bg-cyan-500/5 border-cyan-500/20'
                    }`}
                    onClick={() => {
                      if (!notif.read) markRead(notif.id);
                      setShowNotifPanel(false);
                      if (notif.device_ref) {
                        setActiveTab('reparations');
                      }
                    }}
                    data-testid={`notif-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${notif.read ? 'bg-gray-600' : 'bg-cyan-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{notif.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-gray-600 mt-1">
                          {notif.created_at ? formatDate(notif.created_at, DATETIME_OPTS) : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL CHANGEMENT DE PIN ═══ */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="pin-change-modal">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-bold text-white mb-2">{t('client.pinChangeTitle')}</h3>
            <p className="text-sm text-gray-400 mb-4">{t('client.pinChangeDesc')}</p>
            <div className="space-y-3">
              <input
                type="password"
                maxLength={4}
                value={newPin}
                onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
                placeholder={t('client.newPin')}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-center tracking-widest text-lg font-mono"
                data-testid="new-pin-input"
              />
              <input
                type="password"
                maxLength={4}
                value={confirmPin}
                onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                placeholder={t('client.confirmPin')}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-center tracking-widest text-lg font-mono"
                data-testid="confirm-pin-input"
              />
              <button
                onClick={handleChangePin}
                disabled={changingPin}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                data-testid="change-pin-submit"
              >
                {changingPin ? t('client.modifying') : t('client.changePin')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatModal
        isOpen={!!chatContext}
        onClose={() => setChatContext(null)}
        relatedType={chatContext?.relatedType}
        relatedId={chatContext?.relatedId}
        title={chatContext?.title}
      />

      <ClientChatWidget api={api} user={user} />

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        accentColor="#dc2626"
      />
    </div>
  );
};

// Order Card Component with payment proof upload
const OrderCard = ({ order, api, openSumupCheckout, paymentLoading, openChat, getTimelineEvents, onRefresh, onlinePaymentEnabled }) => {
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [uploading, setUploading] = useState(false);

  const uploadProof = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      await api.post(`/boutique/commandes/${order.id}/payment-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(t('client.proofSent'));
      onRefresh();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('client.sendError'));
    } finally {
      setUploading(false);
    }
  };

  // Order statuses arrive as backend machine values — map to i18n labels for DISPLAY,
  // falling back to the raw value when unknown.
  const statusLabel = {
    en_attente: t('client.orderStatusEnAttente'),
    payee: t('client.orderStatusPayee'),
    proof_uploaded: t('client.orderStatusProofUploaded'),
    confirmee: t('client.orderStatusConfirmee'),
    preparee: t('client.orderStatusPreparee'),
    expediee: t('client.orderStatusExpediee'),
    livree: t('client.orderStatusLivree'),
    annulee: t('client.orderStatusAnnulee')
  };

  const statusColor = {
    en_attente: 'bg-yellow-500/20 text-yellow-400',
    payee: 'bg-blue-500/20 text-blue-400',
    proof_uploaded: 'bg-orange-500/20 text-orange-400',
    confirmee: 'bg-cyan-500/20 text-cyan-400',
    preparee: 'bg-purple-500/20 text-purple-400',
    expediee: 'bg-indigo-500/20 text-indigo-400',
    livree: 'bg-green-500/20 text-green-400',
    annulee: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="client-device-card" data-testid={`client-order-card-${order.id}`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <span className="client-v2-accent font-bold" data-testid={`client-order-ref-${order.id}`}>{order.ref_commande}</span>
          <p className="text-sm text-gray-400" data-testid={`client-order-date-${order.id}`}>
            {formatDate(order.date_commande)}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColor[order.statut] || 'bg-gray-500/20 text-gray-400'}`} data-testid={`client-order-status-${order.id}`}>
          {statusLabel[order.statut] || order.statut}
        </span>
      </div>

      {/* Tracking Number */}
      {order.tracking_number && (
        <div className="mb-4 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-indigo-400" />
            <div>
              <span className="text-xs text-gray-400">{t('client.trackingNumber')}</span>
              <p className="font-bold text-indigo-400 font-mono text-lg">{order.tracking_number}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">{t('client.trackingHint')}</p>
        </div>
      )}

      {/* Status Progress Steps */}
      <div className="mb-4">
        {(() => {
          const steps = [
            { key: 'en_attente', label: t('client.stepCommande'), icon: '1' },
            { key: 'payee', label: t('client.orderStatusPayee'), icon: '2' },
            { key: 'confirmee', label: t('client.orderStatusConfirmee'), icon: '3' },
            { key: 'expediee', label: t('client.orderStatusExpediee'), icon: '4' },
            { key: 'livree', label: t('client.orderStatusLivree'), icon: '5' }
          ];
          const statusOrder = ['en_attente', 'payee', 'proof_uploaded', 'confirmee', 'preparee', 'expediee', 'livree'];
          const currentIdx = statusOrder.indexOf(order.statut);
          const isAnnulee = order.statut === 'annulee';
          
          return (
            <div className="flex items-center justify-between relative">
              {/* Progress line */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-700 mx-6"></div>
              <div className="absolute top-4 left-0 h-0.5 bg-gradient-to-r from-cyan-500 to-green-500 mx-6 transition-all duration-500" style={{
                width: isAnnulee ? '0%' :
                  currentIdx >= 6 ? 'calc(100% - 3rem)' :
                  currentIdx >= 4 ? 'calc(75% - 2rem)' :
                  currentIdx >= 3 ? 'calc(50% - 1rem)' :
                  currentIdx >= 1 ? 'calc(25%)' : '0%'
              }}></div>
              
              {steps.map((step, i) => {
                const stepIdx = statusOrder.indexOf(step.key);
                const isActive = !isAnnulee && currentIdx >= stepIdx;
                const isCurrent = !isAnnulee && currentIdx === stepIdx;
                return (
                  <div key={step.key} className="flex flex-col items-center relative z-10" data-testid={`order-step-${step.key}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      isAnnulee ? 'bg-red-500/20 border-red-500/50 text-red-400' :
                      isCurrent ? 'bg-cyan-500 border-cyan-400 text-white scale-110 shadow-lg shadow-cyan-500/30' :
                      isActive ? 'bg-green-500/20 border-green-500 text-green-400' :
                      'bg-gray-800 border-gray-600 text-gray-500'
                    }`}>
                      {isActive && !isCurrent ? <CheckCircle className="w-4 h-4" /> : step.icon}
                    </div>
                    <span className={`text-[10px] mt-1 ${isCurrent ? 'text-cyan-400 font-bold' : isActive ? 'text-green-400' : 'text-gray-500'}`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      <div className="space-y-2 mb-4" data-testid={`client-order-items-${order.id}`}>
        {order.items?.map((item, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <span>{item.designation} x{item.quantite}</span>
            <span>{formatCurrency(item.total ?? 0)}</span>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-white/10" data-testid={`client-order-total-${order.id}`}>
        <span className="text-gray-400">{t('client.total')}</span>
        <span className="font-bold text-lg">{formatCurrency(order.total ?? 0)}</span>
      </div>

      {/* Payment proof section */}
      {order.payment_proof && (
        <div className="mt-3 p-3 rounded-xl bg-green-500/5 border border-green-500/20" data-testid={`client-order-proof-${order.id}`}>
          <div className="flex items-center gap-2 text-sm">
            <Image className="w-4 h-4 text-green-400" />
            <span className="text-green-400 font-semibold">{t('client.proofSentLabel')}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {t('client.sentOn')} {formatDate(order.payment_proof.uploaded_at, DATETIME_OPTS)}
            {order.payment_proof.validated && (
              <span className="ml-2 text-green-400 font-bold"> - {t('client.validatedByStaff')}</span>
            )}
          </p>
        </div>
      )}

      {/* Actions */}
      {order.statut === 'en_attente' && (
        <div className="mt-3 space-y-2">
          {onlinePaymentEnabled && (
            <button
              onClick={() => openSumupCheckout('boutique', { order_id: order.id })}
              className="w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 transition-all"
              style={{ background: '#1A4CFF' }}
              data-testid={`sumup-pay-order-${order.id}`}
              disabled={paymentLoading}
            >
              {paymentLoading ? (
                <div className="loading-spinner w-5 h-5"></div>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  {t('client.payWithSumupPlain')}
                </>
              )}
            </button>
          )}

          {!order.payment_proof && (
            <label className="w-full py-3 rounded-xl bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 font-bold flex items-center justify-center gap-2 cursor-pointer border border-orange-500/30 transition-all" data-testid={`upload-proof-order-${order.id}`}>
              {uploading ? (
                <div className="loading-spinner w-5 h-5"></div>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  {t('client.sendProof')}
                </>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadProof(e.target.files)}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      )}

      {/* Timeline + Chat */}
      <div className="mt-4 border-t border-white/10 pt-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-cyan-200">{t('client.history')}</h4>
          <button
            onClick={() => openChat('boutique_order', order.id, t('client.chatOrder', { ref: order.ref_commande }))}
            className="text-xs px-3 py-1.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 flex items-center gap-1.5 transition-colors"
            data-testid={`client-order-chat-btn-${order.id}`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {t('client.contact')}
          </button>
        </div>
        <div className="mt-3 relative pl-4">
          <div className="absolute left-1.5 top-1 bottom-1 w-px bg-gradient-to-b from-cyan-500/50 to-transparent"></div>
          {getTimelineEvents(order).map((event, idx) => (
            <div key={`${order.id}-tl-${idx}`} className="relative pb-3 last:pb-0" data-testid={`order-timeline-event-${idx}`}>
              <div className="absolute -left-[10.5px] mt-1.5 w-2 h-2 rounded-full bg-cyan-400 ring-2 ring-cyan-400/20"></div>
              <div className="ml-2">
                <p className="text-xs font-semibold text-white">{event.label}</p>
                <p className="text-[10px] text-gray-500">{event.timestamp ? formatDate(event.timestamp, DATETIME_OPTS) : ''}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Client Boutique Component
const addressIcon = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('bureau') || l.includes('travail') || l.includes('boulot')) return Briefcase;
  return Home;
};

// Reusable address form (autocomplete + Guyane validation) used by the address book and the cart.
const AddressEditor = ({ api, initial, onCancel, onSaved }) => {
  const { t } = useLanguage();
  const [label, setLabel] = useState(initial?.label || t('client.homeLabel'));
  const [recipient, setRecipient] = useState(initial?.recipient_name || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [query, setQuery] = useState(initial?.address?.label || '');
  const [options, setOptions] = useState([]);
  const [resolved, setResolved] = useState(initial?.address || null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!query || query.trim().length < 3 || (resolved && resolved.label === query)) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await api.get(`/addresses/search?q=${encodeURIComponent(query.trim())}`);
        setOptions(r.data || []);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, api, resolved]);

  const save = async () => {
    const typed = (query || '').trim();
    if (!resolved && typed.length < 5) {
      setError(t('client.enterFullAddress'));
      return;
    }
    if (saving) return;
    setSaving(true);
    setError('');
    // Adresse libre acceptée : si aucune suggestion n'a été choisie, on construit
    // l'objet adresse à partir du texte saisi (pas d'obligation de sélectionner la liste).
    const addressObj = resolved || {
      label: typed,
      city: '',
      postcode: (typed.match(/\b973\d{2}\b/) || [''])[0],
      context: '',
      latitude: null,
      longitude: null,
    };
    try {
      const payload = { label: label || t('client.homeLabel'), recipient_name: recipient, phone, address: addressObj };
      if (initial?.id) {
        await api.put(`/client/addresses/${initial.id}`, payload);
      } else {
        await api.post('/client/addresses', payload);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || t('client.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="address-editor">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('client.addressLabelField')}</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className="input-dark w-full py-2" placeholder={t('client.labelPlaceholder')} data-testid="address-label-input" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('client.phone')}</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-dark w-full py-2" placeholder={t('client.phonePlaceholder')} data-testid="address-phone-input" />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">{t('client.recipient')}</label>
        <input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="input-dark w-full py-2" placeholder={t('client.recipientPlaceholder')} data-testid="address-recipient-input" />
      </div>
      <div className="relative">
        <label className="block text-xs text-gray-400 mb-1">{t('client.deliveryAddress')} *</label>
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setResolved(null); setError(''); }}
          className="input-dark w-full py-2"
          placeholder={t('client.addressInputPlaceholder')}
          data-testid="address-search-input"
        />
        <p className="text-[11px] text-gray-500 mt-1">{t('client.freeAddressHint')}</p>
        {searching && <div className="text-xs text-gray-400 mt-1">{t('client.searching')}</div>}
        {options.length > 0 && (
          <div className="mt-1 rounded-xl border border-white/10 bg-black/70 max-h-48 overflow-y-auto">
            {options.map((opt, i) => (
              <button
                type="button"
                key={`${opt.label}-${i}`}
                onClick={() => { setResolved(opt); setQuery(opt.label); setOptions([]); setError(''); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-white/10"
                data-testid={`address-option-${i}`}
              >
                <div className="font-semibold text-white">{opt.label}</div>
                <div className="text-xs text-gray-400">{opt.context}</div>
              </button>
            ))}
          </div>
        )}
        {resolved && <div className="mt-1 text-xs text-emerald-400">{t('client.addressValidated')} : {resolved.label}</div>}
      </div>
      {error && <div className="text-xs text-red-400" data-testid="address-editor-error">{error}</div>}
      <div className="flex gap-2 pt-1">
        <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl client-v2-btn-primary font-bold disabled:opacity-50" data-testid="address-save-btn">
          {saving ? t('client.saving') : t('client.save')}
        </button>
        {onCancel && (
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10" data-testid="address-cancel-btn">
            {t('client.cancel')}
          </button>
        )}
      </div>
    </div>
  );
};

// Page "Mes adresses" — carnet d'adresses de livraison.
const ClientAddresses = ({ api }) => {
  const { t } = useLanguage();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | address object | null

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await api.get('/client/addresses');
      setList(r.data || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t('client.confirmDeleteAddress'))) return;
    try { await api.delete(`/client/addresses/${id}`); fetchList(); } catch (e) { alert(e?.response?.data?.detail || t('client.error')); }
  };
  const setDefault = async (id) => {
    try { await api.post(`/client/addresses/${id}/default`); fetchList(); } catch (e) { alert(e?.response?.data?.detail || t('client.error')); }
  };

  return (
    <div className="space-y-5 fade-in" data-testid="client-addresses-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-white">{t('client.myAddresses')}</h2>
          <p className="text-sm text-gray-400">{t('client.manageAddressesSubtitle')}</p>
        </div>
        {!editing && (
          <button onClick={() => setEditing('new')} className="px-4 py-2 rounded-xl client-v2-btn-primary font-bold flex items-center gap-2" data-testid="address-add-btn">
            <Plus className="w-4 h-4" /> {t('client.addAddress')}
          </button>
        )}
      </div>

      {editing && (
        <div className="card-dark p-5" data-testid="address-editor-card">
          <h3 className="font-bold text-white mb-3">{editing === 'new' ? t('client.newAddress') : t('client.editAddress')}</h3>
          <AddressEditor
            api={api}
            initial={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); fetchList(); }}
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="loading-spinner" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((a) => {
            const Icon = addressIcon(a.label);
            return (
              <div key={a.id} className="card-dark p-5" data-testid={`address-card-${a.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-red-400" />
                    <span className="font-bold text-white">{a.label}</span>
                  </div>
                  {a.is_default && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      <CheckCircle className="w-3 h-3" /> {t('client.default')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-300 space-y-0.5">
                  {a.recipient_name && <div className="text-white font-medium">{a.recipient_name}</div>}
                  {a.phone && <div className="text-gray-400">{a.phone}</div>}
                  <div className="text-gray-400">{a.address?.label}</div>
                </div>
                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-white/10 text-xs">
                  <button onClick={() => setEditing(a)} className="flex items-center gap-1 text-gray-300 hover:text-white" data-testid={`address-edit-${a.id}`}>
                    <Edit3 className="w-3.5 h-3.5" /> {t('client.edit')}
                  </button>
                  <button onClick={() => remove(a.id)} className="flex items-center gap-1 text-red-400 hover:text-red-300" data-testid={`address-delete-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> {t('client.delete')}
                  </button>
                  {!a.is_default && (
                    <button onClick={() => setDefault(a.id)} className="ml-auto underline text-gray-400 hover:text-white" data-testid={`address-default-${a.id}`}>
                      {t('client.setDefault')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!editing && (
            <button
              onClick={() => setEditing('new')}
              className="card-dark p-5 border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-white hover:border-white/30 min-h-[140px]"
              data-testid="address-add-tile"
            >
              <Plus className="w-7 h-7" />
              <span className="text-sm font-semibold">{t('client.addAddress')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ClientBoutique = ({ api, openSumupCheckout, paymentLoading, onlinePaymentEnabled, onManageAddresses }) => {
  const { t, tError, formatCurrency } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [cart, setCart] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [orderError, setOrderError] = useState('');

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchAddresses = async (preselect = true) => {
    setAddressesLoading(true);
    try {
      const r = await api.get('/client/addresses');
      const list = r.data || [];
      setSavedAddresses(list);
      if (preselect) {
        const def = list.find(a => a.is_default) || list[0];
        setSelectedAddressId(def ? def.id : null);
      }
      return list;
    } catch {
      setSavedAddresses([]);
      return [];
    } finally {
      setAddressesLoading(false);
    }
  };

  useEffect(() => {
    if (showCheckout) fetchAddresses(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout]);

  const fetchArticles = async () => {
    try {
      const response = await api.get('/boutique/articles');
      setArticles(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (article) => {
    const existing = cart.find(i => i.id === article.id);
    if (existing) {
      setCart(cart.map(i => i.id === article.id ? { ...i, quantite: i.quantite + 1 } : i));
    } else {
      setCart([...cart, { ...article, quantite: 1 }]);
    }
  };

  const removeFromCart = (id) => setCart(cart.filter(i => i.id !== id));

  const updateQuantity = (id, delta) => {
    setCart(cart.map(i => i.id === id ? { ...i, quantite: Math.max(1, i.quantite + delta) } : i));
  };

  const cartTotal = cart.reduce((sum, i) => sum + (i.prix_vente * i.quantite), 0);

  const resetCheckout = () => {
    setShowCheckout(false);
    setShowAddAddress(false);
    setOrderError('');
  };

  const selectedAddress = savedAddresses.find(a => a.id === selectedAddressId) || null;

  const handleOrder = async (payOnline = false) => {
    if (!selectedAddress) {
      setOrderError(t('client.selectAddress'));
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setOrderError('');

    try {
      const createRes = await api.post('/boutique/commande', {
        items: cart.map(i => ({ article_id: i.id, quantite: i.quantite })),
        adresse_livraison: selectedAddress.address,
        mode_paiement: payOnline ? 'En ligne - SumUp' : 'Paiement à la livraison'
      });

      if (payOnline) {
        const order = createRes.data;
        setCart([]);
        resetCheckout();
        await openSumupCheckout('boutique', { order_id: order.id });
      } else {
        alert(t('client.orderSuccess'));
        setCart([]);
        resetCheckout();
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('client.orderError'));
    } finally {
      setSubmitting(false);
    }
  };

  const categories = Array.from(new Set(articles.map((a) => a.categorie).filter(Boolean))).sort();
  const visibleArticles = categoryFilter === 'all' ? articles : articles.filter((a) => a.categorie === categoryFilter);
  const cartCount = cart.reduce((sum, i) => sum + i.quantite, 0);

  const cartImage = (item) => item.image_url || item.image || (Array.isArray(item.photos) ? item.photos[0] : null);

  return (
    <div className="fade-in space-y-6" data-testid="client-boutique">
      {/* Section header + filtre catégorie + panier */}
      <div className="flex flex-wrap items-end justify-between gap-3" data-testid="client-boutique-header">
        <div>
          <h2 className="text-xl font-black text-white" data-testid="client-boutique-title">{t('client.ourAccessories')}</h2>
          <p className="text-sm text-gray-400">{t('client.expertSelection')}</p>
        </div>
        <div className="flex items-center gap-2">
          {categories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-dark py-2 px-3 text-sm rounded-xl"
              data-testid="client-boutique-category-filter"
            >
              <option value="all">{t('client.allCategories')}</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button
            onClick={() => { resetCheckout(); setShowCheckout(true); }}
            disabled={cart.length === 0}
            className="px-4 py-2 rounded-xl client-v2-btn-primary font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="client-boutique-open-cart"
          >
            <ShoppingBag className="w-5 h-5" />
            <span>{t('client.cart')}{cartCount > 0 ? ` (${cartCount})` : ''}</span>
            {cartCount > 0 && <span className="text-red-100/90">· {formatCurrency(cartTotal)}</span>}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12" data-testid="client-boutique-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : visibleArticles.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="client-boutique-empty">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p data-testid="client-boutique-empty-text">{t('client.noArticles')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="client-boutique-grid">
          {visibleArticles.map((article) => {
            const articleImage = article.image_url || article.image || (Array.isArray(article.photos) ? article.photos[0] : null);
            const inStock = (article.quantite ?? 0) > 0;
            return (
            <div key={article.id} className="product-card flex flex-col" data-testid={`client-boutique-article-${article.id}`}>
              <div className="product-card-image relative" data-testid={`client-boutique-article-image-${article.id}`}>
                {articleImage ? (
                  <img
                    src={articleImage}
                    alt={article.designation}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex'); }}
                  />
                ) : null}
                <div className="w-full h-full items-center justify-center" style={{ display: articleImage ? 'none' : 'flex' }}>
                  <Package className="w-16 h-16 text-red-400/25" />
                </div>
                <span
                  className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${inStock ? 'bg-emerald-500/90 text-white' : 'bg-gray-600/90 text-gray-200'}`}
                  data-testid={`client-boutique-stock-${article.id}`}
                >
                  {inStock ? t('client.inStock') : t('client.unavailable')}
                </span>
              </div>
              <div className="product-card-body flex flex-col flex-1" data-testid={`client-boutique-article-body-${article.id}`}>
                <h3 className="font-bold mb-1 line-clamp-1">{article.designation}</h3>
                {(article.marque || article.modele_compatible) && (
                  <p className="text-xs text-gray-500 mb-3 line-clamp-1">{[article.marque, article.modele_compatible].filter(Boolean).join(' · ')}</p>
                )}
                <div className="flex items-center justify-between mt-auto">
                  <span className="text-xl font-black text-red-400">{formatCurrency(article.prix_vente)}</span>
                  <button
                    onClick={() => addToCart(article)}
                    disabled={!inStock}
                    className="px-3.5 py-2 rounded-xl client-v2-btn-primary text-sm font-bold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`client-boutique-add-${article.id}`}
                  >
                    <ShoppingBag className="w-4 h-4" /> {t('client.add')}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <div className="client-v2-trust-bar" data-testid="client-boutique-trust-bar">
        {BOUTIQUE_TRUST_ITEMS.map((item) => (
          <div key={item.label} className="client-v2-trust-item">
            <strong>{item.label}</strong>
            {item.desc}
          </div>
        ))}
      </div>

      {/* Panier — tiroir latéral droit (style e-commerce) */}
      {showCheckout && (
        <>
          <div className="client-v2-cart-backdrop" onClick={resetCheckout} data-testid="client-boutique-cart-backdrop" />
          <aside className="client-v2-cart-drawer" data-testid="client-boutique-cart-modal">
            <div className="client-v2-cart-header">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-red-400" />
                <h3 className="font-bold text-lg" data-testid="client-boutique-cart-title">{t('client.myCart')}</h3>
                <span className="text-sm text-gray-400">· {cartCount} {cartCount > 1 ? t('client.articlesPlural') : t('client.articleSingular')}</span>
              </div>
              <button onClick={resetCheckout} className="text-gray-400 hover:text-white" data-testid="client-boutique-cart-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="client-boutique-cart-items">
              {cart.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{t('client.cartEmpty')}</p>
                </div>
              ) : cart.map((item) => {
                const thumb = cartImage(item);
                return (
                <div key={item.id} className="flex gap-3 p-3 rounded-xl bg-white/5" data-testid={`client-boutique-cart-item-${item.id}`}>
                  <div className="w-14 h-14 rounded-lg bg-black/30 overflow-hidden flex items-center justify-center shrink-0">
                    {thumb ? (
                      <img src={thumb} alt={item.designation} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-6 h-6 text-red-400/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-white line-clamp-1">{item.designation}</div>
                    <div className="text-sm text-red-400 font-bold">{formatCurrency(item.prix_vente)}</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => updateQuantity(item.id, -1)} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-gray-200" data-testid={`client-boutique-qty-decrease-${item.id}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{item.quantite}</span>
                      <button onClick={() => updateQuantity(item.id, 1)} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-gray-200" data-testid={`client-boutique-qty-increase-${item.id}`}>
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeFromCart(item.id)} className="ml-auto w-7 h-7 rounded-lg bg-red-500/15 hover:bg-red-500/25 flex items-center justify-center text-red-400" data-testid={`client-boutique-remove-${item.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-white/10 p-5 space-y-4" data-testid="client-boutique-cart-summary">
                <div className="flex justify-between text-xl font-black">
                  <span className="text-white">{t('client.total')}</span>
                  <span className="text-red-400">{formatCurrency(cartTotal)}</span>
                </div>

                <div data-testid="client-boutique-address-block">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-gray-300">{t('client.deliveryAddress')}</label>
                    {onManageAddresses && (
                      <button onClick={() => { resetCheckout(); onManageAddresses(); }} className="text-xs text-red-400 hover:text-red-300" data-testid="client-boutique-manage-addresses">
                        {t('client.manage')}
                      </button>
                    )}
                  </div>

                  {addressesLoading ? (
                    <div className="flex justify-center py-4"><div className="loading-spinner w-5 h-5" /></div>
                  ) : (
                    <div className="space-y-2">
                      {savedAddresses.map((a) => {
                        const Icon = addressIcon(a.label);
                        const active = a.id === selectedAddressId;
                        return (
                          <button
                            type="button"
                            key={a.id}
                            onClick={() => setSelectedAddressId(a.id)}
                            className={`w-full text-left p-3 rounded-xl border flex gap-3 transition-all ${active ? 'border-red-500/60 bg-red-500/5' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                            data-testid={`client-boutique-address-${a.id}`}
                          >
                            <span className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${active ? 'border-red-500' : 'border-gray-500'}`}>
                              {active && <span className="w-2 h-2 rounded-full bg-red-500" />}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-red-400" />
                                <span className="font-bold text-white text-sm">{a.label}</span>
                                {a.is_default && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{t('client.default')}</span>}
                              </div>
                              {a.recipient_name && <div className="text-xs text-gray-400 mt-0.5">{a.recipient_name}{a.phone ? ` · ${a.phone}` : ''}</div>}
                              <div className="text-xs text-gray-400 truncate">{a.address?.label}</div>
                            </div>
                          </button>
                        );
                      })}

                      {showAddAddress ? (
                        <div className="p-3 rounded-xl border border-white/10 bg-white/5">
                          <AddressEditor
                            api={api}
                            onCancel={() => setShowAddAddress(false)}
                            onSaved={async () => {
                              setShowAddAddress(false);
                              const list = await fetchAddresses(false);
                              const last = list[list.length - 1];
                              if (last) setSelectedAddressId(last.id);
                            }}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAddAddress(true)}
                          className="w-full p-3 rounded-xl border border-dashed border-white/20 text-gray-300 hover:text-white hover:border-white/40 flex items-center justify-center gap-2 text-sm"
                          data-testid="client-boutique-add-address"
                        >
                          <Plus className="w-4 h-4" /> {t('client.addAddress')}
                        </button>
                      )}
                    </div>
                  )}

                  {orderError && (
                    <div className="mt-2 text-xs text-red-400" data-testid="client-boutique-order-error">{orderError}</div>
                  )}
                </div>

                <button
                  onClick={() => handleOrder(true)}
                  className="w-full py-3 rounded-xl text-white font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                  style={{ background: '#1A4CFF' }}
                  data-testid="client-boutique-sumup-submit"
                  disabled={paymentLoading || submitting}
                >
                  {paymentLoading ? (
                    <div className="loading-spinner w-5 h-5"></div>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      {t('client.payWithSumupPlain')}
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleOrder(false)}
                  className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-all disabled:opacity-50"
                  data-testid="client-boutique-cod-submit"
                  disabled={submitting}
                >
                  <Truck className="w-5 h-5" />
                  {submitting ? t('client.processing') : t('client.payOnDelivery')}
                </button>
              </div>
            )}
          </aside>
        </>
      )}

    </div>
  );
};

export default ClientPortalPage;
